import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  message,
  Modal,
  Row,
  Space,
  Table,
  Upload
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch, useSelector } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';

import {
  fetchsClientAccountsHandler,
  selectClientAccounts
} from '../../../redux/settings/carriersSlice';
import { selectCurUser } from '../../../redux/user/userSlice';

interface LabelUrlItem {
  labelUrl: string;
}

interface Labels {
  trackingId: string;
}

interface SuccessResponseData {
  trackingNumber?: string;
  labelUrlList?: LabelUrlItem[];
  labels?: Labels[];
  [key: string]: any;
}

interface BatchOrderItem {
  trackingNumber?: string; // 运单号（系统）
  channel: string; // 渠道代码
  receiverName: string; // 收件人
  receiverCompany?: string; // 收件公司
  country: string; // 国家二字码
  province: string; // 省/州二字码
  city: string; // 城市
  address1: string; // 地址1
  address2?: string; // 地址2
  phone: string; // 电话号码
  zipCode: string; // 邮编
  email?: string; // 电子邮件
  packageType: string; // 包裹类型
  goodsDescription: string; // 货物描述

  // 包裹信息1
  weight1: number; // 包裹单件重量(KG)
  length1?: number; // 包裹长(CM)
  width1?: number; // 包裹宽(CM)
  height1?: number; // 包裹高(CM)
  quantity1: number; // 包裹数
  count1: number; // 包裹件数

  // 包裹信息2（可选）
  weight2?: number;
  length2?: number;
  width2?: number;
  height2?: number;
  quantity2?: number;
  count2: number; // 包裹件数

  // 申报信息
  declarationItems: {
    itemName: string; // 英文品名
    unitPrice: number; // 单价
    quantity: number; // 数量
    weight: number; // 重量
  }[];
}

enum UnitMeasure {
  LbIn = 0,
  LbKg = 1
}

const BathOrderPage = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const clientAccounts = useSelector(selectClientAccounts);
  const user = useSelector(selectCurUser);

  const clientToken = user.apiToken;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [successData, setSuccessData] = useState<SuccessResponseData[]>([]);
  const [batchOrders, setBatchOrders] = useState<BatchOrderItem[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);

  useEffect(() => {
    dispatch(fetchsClientAccountsHandler());
    if (clientToken) {
      form.setFieldsValue({
        token: clientToken
      });
    }
  }, [dispatch, form, clientToken]);

  // 修复文件上传处理
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        if (!content) {
          message.error('文件内容为空');
          return;
        }

        // 处理CSV格式 - 使用逗号分隔
        const lines = content.split('\n').filter((line) => line.trim());
        if (lines.length <= 1) {
          message.error('文件数据不足，请检查文件格式');
          return;
        }

        const orders: BatchOrderItem[] = [];

        // 从第二行开始（跳过表头）
        for (let i = 1; i < lines.length; i += 1) {
          const line = lines[i].trim();
          if (line) {
            // CSV格式使用逗号分隔，并处理引号
            const columns = line.split(',').map((col) => {
              // 去除引号和前后空格
              return col.replace(/^"|"$/g, '').trim();
            });

            // 验证必要字段
            const hasRequiredFields =
              columns[1] &&
              columns[2] &&
              columns[4] &&
              columns[5] &&
              columns[6] &&
              columns[7] &&
              columns[9] &&
              columns[10];

            if (hasRequiredFields) {
              try {
                const order: BatchOrderItem = {
                  trackingNumber: columns[0] || undefined,
                  channel: columns[1],
                  receiverName: columns[2],
                  receiverCompany: columns[3] || undefined,
                  country: columns[4],
                  province: columns[5],
                  city: columns[6],
                  address1: columns[7],
                  address2: columns[8] || undefined,
                  phone: columns[9],
                  zipCode: columns[10],
                  email: columns[11] || undefined,
                  packageType: columns[12] || '02',
                  goodsDescription: columns[13] || 'Product',

                  // 包裹信息1
                  weight1: parseFloat(columns[14]) || 0,
                  length1: parseFloat(columns[15]) || undefined,
                  width1: parseFloat(columns[16]) || undefined,
                  height1: parseFloat(columns[17]) || undefined,
                  quantity1: parseInt(columns[18], 10) || 1,
                  count1: parseInt(columns[19], 10) || 1,

                  // 包裹信息2
                  weight2: columns[20] ? parseFloat(columns[20]) : undefined,
                  length2: columns[21] ? parseFloat(columns[21]) : undefined,
                  width2: columns[22] ? parseFloat(columns[22]) : undefined,
                  height2: columns[23] ? parseFloat(columns[23]) : undefined,
                  quantity2: columns[24]
                    ? parseInt(columns[24], 10)
                    : undefined,
                  count2: parseInt(columns[25], 10) || 1,

                  // 申报信息
                  declarationItems: [
                    {
                      itemName: columns[26] || 'Product',
                      unitPrice: parseFloat(columns[27]) || 0,
                      quantity: parseInt(columns[28], 10) || 1,
                      weight: parseFloat(columns[29]) || 0.01
                    }
                  ]
                };

                orders.push(order);
              } catch (error) {
                console.error(`解析第 ${i + 1} 行数据时出错:`, error);
              }
            } else {
              console.warn(`跳过第 ${i + 1} 行数据，缺少必要字段`, columns);
            }
          }
        }

        if (orders.length === 0) {
          message.error('没有成功解析出任何订单数据，请检查文件格式');
          return;
        }

        setBatchOrders(orders);
        message.success(`成功导入 ${orders.length} 个订单`);
      } catch (error) {
        message.error('文件解析失败，请检查文件格式');
        console.error('文件解析错误:', error);
      }
    };

    reader.onerror = () => {
      message.error('文件读取失败');
      setFileList([]);
    };

    reader.readAsText(file);
  };

  // 修复上传组件配置
  const uploadProps = {
    accept: '.csv',
    fileList,
    beforeUpload: (file: File) => {
      // 验证文件类型
      const isValidType =
        file.name.endsWith('.csv') || file.type === 'text/csv';
      if (!isValidType) {
        message.error('只能上传CSV文件!');
        return false;
      }

      // 验证文件大小 (5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('文件大小不能超过5MB!');
        return false;
      }

      // 设置文件列表
      setFileList([file]);

      // 处理文件
      handleFileUpload(file);

      // 返回false阻止自动上传
      return false;
    },
    onRemove: () => {
      setFileList([]);
      setBatchOrders([]);
    }
  };

  // 批量提交订单
  const handleBatchSubmit = async () => {
    if (batchOrders.length === 0) {
      message.warning('请先导入订单数据');
      return;
    }

    if (isSubmitting) {
      message.warning('请勿重复提交');
      return;
    }

    setIsSubmitting(true);
    const results: SuccessResponseData[] = [];
    let successCount = 0;
    let failCount = 0;

    try {
      const processOrders = async () => {
        const promises = batchOrders.map(async (order, index) => {
          try {
            // 构建包裹列表
            const packageList = [];

            // 第一个包裹
            packageList.push({
              weight: order.weight1,
              count: order.count1,
              ...(order.length1 && { length: order.length1 }),
              ...(order.width1 && { width: order.width1 }),
              ...(order.height1 && { height: order.height1 }),
              lineItems: [
                {
                  name: order.goodsDescription,
                  totalValue: 100,
                  quantity: order.quantity1
                }
              ]
            });

            // 第二个包裹（如果有）
            if (order.weight2) {
              packageList.push({
                weight: order.weight2,
                count: order.count2,
                ...(order.length2 && { length: order.length2 }),
                ...(order.width2 && { width: order.width2 }),
                ...(order.height2 && { height: order.height2 }),
                lineItems: [
                  {
                    name: order.goodsDescription,
                    totalValue: 100,
                    quantity: order.quantity2 || 1
                  }
                ]
              });
            }

            const apiData = {
              token: clientToken,
              channelId: order.channel,
              orderId:
                order.trackingNumber ||
                `BATCH_${Date.now()}_${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
              test: false,
              packageType: order.packageType || '02',
              shipTo: {
                name: order.receiverName,
                company: order.receiverCompany || '',
                phone: order.phone,
                email: order.email || '',
                address1: order.address1,
                address2: order.address2 || '',
                city: order.city,
                state: order.province,
                country: order.country,
                zipCode: order.zipCode
              },
              packageList,
              invoice: {
                currencyCode: 'USD',
                invoiceDetailList: order.declarationItems.map((item) => ({
                  descriptionEn: item.itemName,
                  currencyValue: Number(item.unitPrice),
                  weight: item.weight,
                  unitCount: Number(item.quantity)
                }))
              }
            };

            const response = await fetch(
              'https://api.eksshipping.com/api/label',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(apiData)
              }
            );

            const result = await response.json();

            if (response.ok) {
              results.push(result);
              successCount += 1;
            } else {
              console.error(`订单 ${order.trackingNumber} 创建失败:`, result);
              failCount += 1;
            }
          } catch (error) {
            console.error(`处理订单 ${index} 时出错:`, error);
            failCount += 1;
          }
        });

        await Promise.all(promises);
      };

      await processOrders();

      setSuccessData(results);
      setIsSuccessModalVisible(true);

      if (failCount === 0) {
        message.success(`所有 ${successCount} 个订单创建成功！`);
      } else {
        message.warning(`${successCount} 个成功, ${failCount} 个失败`);
      }
    } catch (error) {
      console.error('批量提交出错:', error);
      message.error('批量提交过程中出现错误');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 重置表单
  const resetForm = () => {
    setBatchOrders([]);
    setSuccessData([]);
    setFileList([]);
  };

  // 处理成功弹框确定
  const handleSuccessModalOk = () => {
    setIsSuccessModalVisible(false);
    resetForm();
  };

  // 下载模板
  const downloadTemplate = () => {
    const headers = [
      '运单号（系统）',
      '渠道代码（渠道）',
      '收件人',
      '收件公司',
      '国家二字码',
      '省/州二字码',
      '城市',
      '地址1',
      '地址2',
      '电话号码',
      '邮编',
      '电子邮件',
      '包裹类型',
      '货物描述',
      '包裹单件重量(lb)',
      '包裹长(in)',
      '包裹宽(in)',
      '包裹高(in)',
      '包裹数量',
      '包裹件数',
      '包裹单件重量(lb)2',
      '包裹长(in)2',
      '包裹宽(in)2',
      '包裹高(in)2',
      '包裹数2',
      '包裹件数2',
      '申报信息英文品名',
      '单价',
      '数量',
      '申报重量'
    ];

    // 使用逗号分隔并添加引号来处理可能包含逗号的内容
    const templateContent = `${headers
      .map((header) => `"${header}"`)
      .join(',')}\n`;
    const blob = new Blob([templateContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '批量下单模板.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Card title="批量创建订单" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="上传订单文件">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    accept={uploadProps.accept}
                    fileList={uploadProps.fileList}
                    beforeUpload={uploadProps.beforeUpload}
                    onRemove={uploadProps.onRemove}
                  >
                    <Button icon={<UploadOutlined />}>选择文件</Button>
                  </Upload>
                  <Button type="link" onClick={downloadTemplate}>
                    下载模板文件
                  </Button>
                </Space>
                <div style={{ marginTop: 8, color: '#999' }}>
                  支持CSV格式，请使用逗号分隔各列
                </div>
              </Form.Item>
            </Col>
          </Row>

          {batchOrders.length > 0 && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={`已导入订单 (${batchOrders.length} 个)`}>
                  <Table
                    dataSource={batchOrders.map((order, index) => ({
                      ...order,
                      key: index
                    }))}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 1200 }}
                    size="small"
                    columns={[
                      {
                        title: '运单号',
                        dataIndex: 'trackingNumber',
                        width: 120,
                        render: (text) => text || '系统生成'
                      },
                      {
                        title: '渠道',
                        dataIndex: 'channel',
                        width: 80
                      },
                      {
                        title: '收件人',
                        dataIndex: 'receiverName',
                        width: 100
                      },
                      {
                        title: '国家',
                        dataIndex: 'country',
                        width: 80
                      },
                      {
                        title: '城市',
                        dataIndex: 'city',
                        width: 100
                      },
                      {
                        title: '电话',
                        dataIndex: 'phone',
                        width: 120
                      },
                      {
                        title: '重量',
                        dataIndex: 'weight1',
                        width: 60,
                        render: (weight) => `${weight}lb`
                      },
                      {
                        title: '长',
                        dataIndex: 'length1',
                        width: 60,
                        render: (length) => `${length}in`
                      },
                      {
                        title: '宽',
                        dataIndex: 'width1',
                        width: 60,
                        render: (width) => `${width}in`
                      },
                      {
                        title: '高',
                        dataIndex: 'height1',
                        width: 60,
                        render: (height) => `${height}in`
                      }
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={handleBatchSubmit}
                loading={isSubmitting}
                disabled={batchOrders.length === 0 || isSubmitting}
              >
                {isSubmitting
                  ? '提交中...'
                  : `批量提交 (${batchOrders.length})`}
              </Button>
              <Button onClick={resetForm} disabled={isSubmitting}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 成功弹框 */}
      <Modal
        title="批量订单创建结果"
        visible={isSuccessModalVisible}
        onOk={handleSuccessModalOk}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText="确定"
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>批量订单处理完成！</p>
        </div>
        {successData.length > 0 && (
          <Table
            dataSource={successData.map((item, index) => ({
              ...item,
              key: index
            }))}
            pagination={{ pageSize: 5 }}
            size="small"
            columns={[
              {
                title: '跟踪号码',
                dataIndex: 'labels',
                render: (labels: Labels[]) =>
                  labels && labels.length > 0
                    ? labels.map((label) => label.trackingId).join(', ')
                    : 'N/A'
              },
              {
                title: 'URL',
                dataIndex: 'labelUrlList',
                render: (labelUrlList: LabelUrlItem[]) =>
                  labelUrlList && labelUrlList.length > 0 ? (
                    <a
                      href={labelUrlList[0].labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {labelUrlList[0].labelUrl}
                    </a>
                  ) : (
                    'N/A'
                  )
              },
              {
                title: '标签URL',
                dataIndex: 'labelUrlList',
                render: (labelUrlList: LabelUrlItem[]) =>
                  labelUrlList && labelUrlList.length > 0 ? (
                    <a
                      href={labelUrlList[0].labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      查看标签
                    </a>
                  ) : (
                    'N/A'
                  )
              }
            ]}
          />
        )}
      </Modal>
    </>
  );
};

export default BathOrderPage;
